import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchSmsPreferences, updateSmsPreferences, disconnectGoogle } from "@/lib/api";
import { useAuth } from "@/app/AuthContext";

/** Visible legal link — a real link when configured, else a clearly-marked
 *  placeholder (never an invented legal URL). */
function LegalLink({ label, url }: { label: string; url: string }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="underline hover:opacity-80">
        {label}
      </a>
    );
  }
  return (
    <span className="underline decoration-dotted opacity-70" title="URL not configured">
      {label} (link not configured)
    </span>
  );
}

/** Connect / disconnect "Sign in with Google" for the current account. Shown
 *  only when Google login is configured on the backend. */
function ConnectedAccountsCard() {
  const { googleLoginEnabled, googleLinked, refetch } = useAuth();

  // Surface the outcome of a Connect round trip (the callback redirects back to
  // /account?google=...), then strip the param so a refresh doesn't re-toast.
  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("google");
    if (!code) return;
    if (code === "connected") {
      toast.success("Google account connected.");
      refetch();
    } else if (code === "already_linked") {
      toast.error("That Google account is already linked to a different user.");
    } else if (code === "disabled" || code === "google_disabled") {
      toast.error("Google sign-in is not enabled.");
    } else {
      toast.error("Google account was not connected. Please try again.");
    }
    window.history.replaceState({}, "", "/account");
  }, [refetch]);

  const disconnectM = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: () => {
      toast.success("Google account disconnected.");
      refetch();
    },
    onError: (e: any) =>
      toast.error(e?.message || "Could not disconnect Google"),
  });

  if (!googleLoginEnabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected accounts</CardTitle>
        <CardDescription>
          Link a Google account so you can also sign in with Google.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">Google</span>
            {googleLinked ? (
              <Badge variant="secondary">Connected</Badge>
            ) : (
              <Badge variant="outline">Not connected</Badge>
            )}
          </div>
          {googleLinked ? (
            <Button
              variant="outline"
              onClick={() => disconnectM.mutate()}
              disabled={disconnectM.isPending}
            >
              {disconnectM.isPending ? "Disconnecting…" : "Disconnect"}
            </Button>
          ) : (
            <Button onClick={() => { window.location.href = "/auth/google/connect"; }}>
              Connect Google
            </Button>
          )}
        </div>
        <p className="mt-2 text-xs opacity-60">
          Google is matched to your account; you can still sign in with your
          password. Disconnecting is blocked if Google is your only sign-in
          method — set a password first.
        </p>
      </CardContent>
    </Card>
  );
}

export function AccountSettingsPage() {
  const qc = useQueryClient();
  const prefsQ = useQuery({ queryKey: ["sms-preferences"], queryFn: fetchSmsPreferences });
  const p = prefsQ.data;

  const [checked, setChecked] = React.useState(false);
  React.useEffect(() => {
    if (p) setChecked(p.consent_active);
  }, [p?.consent_active]);

  const saveM = useMutation({
    mutationFn: (optIn: boolean) => updateSmsPreferences(optIn),
    onSuccess: () => {
      toast.success("Your VoltiE text message preferences have been updated.");
      qc.invalidateQueries({ queryKey: ["sms-preferences"] });
      qc.invalidateQueries({ queryKey: ["reset-availability"] });
    },
    onError: (e: any) =>
      toast.error(e?.body?.message || e?.message || "Could not update your preferences"),
  });

  const canCheck = Boolean(p?.opt_in_eligible);
  // Backend kill switch: when the SMS channel is disabled the endpoint returns
  // only { feature_enabled: false } and the opt-in section is hidden entirely.
  const smsDisabled = p != null && p.feature_enabled === false;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
      <h1 className="text-xl font-semibold">Account Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>VoltiE Text Message Preferences</CardTitle>
          <CardDescription>
            Manage whether VoltiE may send you transactional (non-promotional) text messages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {prefsQ.isLoading && <div className="text-sm opacity-70">Loading…</div>}
          {prefsQ.isError && (
            <div className="text-sm text-red-600 dark:text-red-400">
              Could not load your text-message preferences.
            </div>
          )}

          {smsDisabled && (
            <div className="text-sm opacity-70">
              Text-message preferences aren't available right now.
            </div>
          )}

          {p && !smsDisabled && (
            <div className="space-y-4">
              {/* Phone number + verification */}
              <div>
                <div className="text-xs uppercase opacity-60">Phone number</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-medium">{p.masked_phone || "No phone on file"}</span>
                  {p.phone_verified ? (
                    <Badge variant="secondary">Verified</Badge>
                  ) : (
                    <Badge variant="outline">Not verified</Badge>
                  )}
                </div>
                {!canCheck && (
                  <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    A verified, valid phone number is required before you can enable text messages.
                  </div>
                )}
              </div>

              {/* Opt-in checkbox + full disclosure (all visible for the screenshot) */}
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={checked}
                  disabled={!canCheck || saveM.isPending}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span className="text-sm font-medium">
                  Receive transactional text messages from VoltiE
                </span>
              </label>

              <p className="whitespace-pre-line text-xs leading-relaxed opacity-80">
                {p.disclosure_text}
              </p>

              <div className="text-xs">
                <LegalLink label="Privacy Policy" url={p.privacy_policy_url} />
                <span className="px-2 opacity-40">|</span>
                <LegalLink label="Terms of Service" url={p.terms_of_service_url} />
              </div>

              <Button
                onClick={() => saveM.mutate(checked)}
                disabled={saveM.isPending || (!canCheck && !p.consent_active)}
              >
                {saveM.isPending ? "Saving…" : "Save SMS preferences"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ConnectedAccountsCard />
    </div>
  );
}
